import type { ObservationWinRatePoint } from './observation-analysis';

export function observationTrendMoveAtRatio(ratio: number, lastMoveNumber: number): number {
  return Math.round(Math.min(1, Math.max(0, ratio)) * Math.max(0, lastMoveNumber));
}

/** Keep unanalysed moves as gaps; connecting them would suggest data we do not have. */
export function observationTrendSegments(
  points: readonly ObservationWinRatePoint[],
  lastMoveNumber: number,
): readonly (readonly ObservationWinRatePoint[])[] {
  const byMove = new Map<number, ObservationWinRatePoint>();
  for (const point of points) {
    if (point.moveNumber >= 0 && point.moveNumber <= lastMoveNumber) {
      byMove.set(point.moveNumber, point);
    }
  }
  const segments: ObservationWinRatePoint[][] = [];
  for (const point of [...byMove.values()].sort(
    (left, right) => left.moveNumber - right.moveNumber,
  )) {
    const previous = segments.at(-1);
    if (previous?.at(-1)?.moveNumber === point.moveNumber - 1) previous.push(point);
    else segments.push([point]);
  }
  return segments;
}

export function observationTrendScoreLimit(points: readonly ObservationWinRatePoint[]): number {
  const largest = points.reduce(
    (maximum, point) => Math.max(maximum, Math.abs(point.blackScoreLeadHalfPoints) / 2),
    0,
  );
  return Math.max(20, Math.ceil(largest / 10) * 10);
}

export function observationTrendMoveTicks(lastMoveNumber: number): readonly number[] {
  if (lastMoveNumber <= 0) return [0];
  if (lastMoveNumber <= 4) {
    return Array.from({ length: lastMoveNumber + 1 }, (_, index) => index);
  }
  const magnitude = 10 ** Math.floor(Math.log10(lastMoveNumber / 4));
  const step = Math.ceil(lastMoveNumber / 4 / magnitude) * magnitude;
  const ticks = [0];
  for (let move = step; move < lastMoveNumber - step * 0.45; move += step) ticks.push(move);
  return [...ticks, lastMoveNumber];
}

export function observationTrendTooltip(
  moveNumber: number,
  point: ObservationWinRatePoint | undefined,
): string {
  if (point === undefined) return `${moveNumber}手 · 尚未分析`;
  const score = point.blackScoreLeadHalfPoints / 2;
  const scoreLabel = score < 0 ? `−${Math.abs(score).toFixed(1)}` : score.toFixed(1);
  return `${moveNumber}手 ${(point.blackWinRateBps / 100).toFixed(1)}% ${scoreLabel}目`;
}
